import React from 'react';
import { Box, Card, Grid, useMediaQuery } from '@mui/material';
import { useTheme } from '@emotion/react';
import Shimmer from 'ui-components/Shimmer';

// Placeholder for RoundInformation.js. cardSx / subCardSx / CardHeader /
// CardContentList are copied from that file so the cards keep their geometry.
const cardSx = (theme) => ({ padding: '1.25rem 1.5rem 1.5rem', boxShadow: 'none', borderRadius: '16px', border: `1px solid ${theme.palette.customColors.gray300}`, overflow: 'hidden' });
const subCardSx = (theme) => ({ padding: '1rem', boxShadow: 'none', borderRadius: '12px', border: `1px solid ${theme.palette.customColors.gray300}`, overflow: 'hidden', marginBottom: '1.25rem' });

// Title (1.2rem/700) over the divider CardHeader draws.
const CardHeaderSkeleton = ({ width = 170 }) => {
  const theme = useTheme();
  return (
    <Box>
      <Box display="flex" alignItems="center" mb="0.75rem" sx={{ height: 26 }}>
        <Shimmer width={width} height={18} radius={4} />
      </Box>
      <Box sx={{ borderBottom: `1px solid ${theme.palette.customColors.gray300}`, mb: '1.25rem' }} />
    </Box>
  );
};

// A row is icon + label + right-aligned value inside 0.875rem 1rem padding.
const ContentListSkeleton = ({ rows = 3 }) => {
  const theme = useTheme();
  return (
    <Grid container xs={12} sx={{ border: `1px solid ${theme.palette.customColors.gray300}`, borderRadius: '12px', overflow: 'hidden' }}>
      {Array.from({ length: rows }).map((_, index) => (
        <Grid
          item
          xs={12}
          key={index}
          display="flex"
          alignItems="center"
          gap="0.875rem"
          sx={{ padding: '0.875rem 1rem', borderTop: index === 0 ? 'none' : `1px solid ${theme.palette.customColors.gray300}` }}
        >
          <Shimmer width={24} height={24} radius={6} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Shimmer width={index % 2 === 0 ? '45%' : '60%'} height={13} radius={4} />
          </Box>
          <Shimmer width={index % 3 === 0 ? 72 : 56} height={13} radius={4} />
        </Grid>
      ))}
    </Grid>
  );
};

// Rich-text block: an icon + heading, then justified body lines.
const HighlightSkeleton = ({ lines = 3 }) => {
  const theme = useTheme();
  return (
    <Card sx={subCardSx(theme)}>
      <Box display="flex" alignItems="center" gap="0.625rem" mb="0.5rem" sx={{ height: 22 }}>
        <Shimmer width={140} height={15} radius={4} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {Array.from({ length: lines }).map((_, i) => (
          <Shimmer key={i} width={i === lines - 1 ? '62%' : '100%'} height={11} radius={4} />
        ))}
      </Box>
    </Card>
  );
};

// The Current Investors table: a filled header row then striped body rows.
const InvestorTableSkeleton = ({ rows = 3 }) => {
  const theme = useTheme();
  return (
    <Grid container xs={12} sx={{ border: `1px solid ${theme.palette.customColors.gray300}`, borderRadius: '12px', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', width: '100%', padding: '12px 16px', backgroundColor: theme.palette.customColors.g200 }}>
        <Box sx={{ flex: 1 }}><Shimmer width={104} height={13} radius={4} /></Box>
        <Box sx={{ flex: 1 }}><Shimmer width={104} height={13} radius={4} /></Box>
      </Box>
      {Array.from({ length: rows }).map((_, index) => (
        <Box
          key={index}
          sx={{ display: 'flex', width: '100%', padding: '16px', backgroundColor: index % 2 === 0 ? theme.palette.customColors.inputField : 'transparent' }}
        >
          <Box sx={{ flex: 1 }}><Shimmer width={index % 2 === 0 ? 120 : 96} height={13} radius={4} /></Box>
          <Box sx={{ flex: 1 }}><Shimmer width={index % 2 === 0 ? 170 : 148} height={13} radius={4} /></Box>
        </Box>
      ))}
    </Grid>
  );
};

const RoundInformationSkeleton = () => {
  const theme = useTheme();
  // RoundInformation moves Fund Utilization into the right column at lg.
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const FundUtilization = (
    <Card sx={cardSx(theme)}>
      <CardHeaderSkeleton width={150} />
      <HighlightSkeleton lines={3} />
      <Shimmer width={130} height={15} radius={4} sx={{ mb: '0.625rem' }} />
      <Card sx={{ padding: '1rem', boxShadow: 'none', borderRadius: '12px', border: `1px solid ${theme.palette.customColors.gray300}`, overflow: 'hidden' }}>
        {/* PieChart is square and fills the card. */}
        <Shimmer width="100%" height="auto" radius={8} sx={{ aspectRatio: '1 / 1' }} />
      </Card>
    </Card>
  );

  return (
    <Grid
      item
      container
      xs={12}
      spacing={'1.25rem'}
      display={'flex'}
      sx={{ paddingBottom: '2rem', flexDirection: 'row' }}
      justifyContent={'space-between'}
    >
      <Grid item container xs={12} sm={12} md={7} lg={7.5} spacing={'1.25rem'} alignSelf={'flex-start'}>
        <Grid item xs={12}>
          <Card sx={cardSx(theme)}>
            <CardHeaderSkeleton width={130} />
            <ContentListSkeleton rows={3} />
          </Card>
        </Grid>

        {!isLargeScreen && <Grid item xs={12}>{FundUtilization}</Grid>}

        <Grid item xs={12}>
          <Card sx={cardSx(theme)}>
            <CardHeaderSkeleton width={210} />
            <HighlightSkeleton lines={2} />
            <ContentListSkeleton rows={6} />
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={cardSx(theme)}>
            <CardHeaderSkeleton width={160} />
            <InvestorTableSkeleton rows={3} />
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={cardSx(theme)}>
            <CardHeaderSkeleton width={230} />
            <HighlightSkeleton lines={3} />
            <ContentListSkeleton rows={4} />
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={cardSx(theme)}>
            <CardHeaderSkeleton width={110} />
            <HighlightSkeleton lines={2} />
            <HighlightSkeleton lines={2} />
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={cardSx(theme)}>
            <CardHeaderSkeleton width={200} />
            <HighlightSkeleton lines={2} />
            <ContentListSkeleton rows={2} />
          </Card>
        </Grid>
      </Grid>

      {isLargeScreen && (
        <Grid item xs={12} sm={12} md={5} lg={4.5}>
          {FundUtilization}
        </Grid>
      )}
    </Grid>
  );
};

export default RoundInformationSkeleton;
